import { useCallback, useReducer } from "react";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { GridApi } from "ag-grid-community";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { actions } from "./UserListReportActions";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type UserListReportStateType = {

    organizationID: number | undefined;
    data: GridData | undefined;
    gridApi: GridApi | undefined;
    downloadUid: string | undefined;
    actions: ActionMenuItem[]
};

const defaultUserListReportState: UserListReportStateType = {
    
    organizationID: undefined,
    data: undefined,
    gridApi: undefined,
    downloadUid: undefined,
    actions: actions
};

export {defaultUserListReportState}

enum UserListReportStateActionType {
    OrganizationChanged = 'USER_LIST_ORGANIZATION_CHANGED',
    DataChanged = 'USER_LIST_REPORT_DATA_CHANGED',
    DownloadUidChanged = 'USER_LIST_REPORT_DOWNLOAD_UID_CHANGED',
    GridApiChanged = 'USER_LIST_REPORT_GRID_API_CHANGED',
    ActionsChanged = 'USER_LIST_REPORT_ACTIONS_CHANGED'
}

type UserListReportStateActions = | {
    type: UserListReportStateActionType.DataChanged;
    payload: GridData;
} | {
    type: UserListReportStateActionType.GridApiChanged;
    payload: GridApi;
} | {
    type: UserListReportStateActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: UserListReportStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: UserListReportStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
};

function userListReportStateReducer(state: UserListReportStateType, 
    action: UserListReportStateActions): UserListReportStateType {

    const copy = {...state};
    switch (action.type) {
        
        case UserListReportStateActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            return copy;

        case UserListReportStateActionType.DataChanged:
            copy.data = action.payload;
            return copy;

        case UserListReportStateActionType.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;

    
        case UserListReportStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case UserListReportStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;
    
        default:
            return state;
    }
}

export default function useUserListReportState() {

    const [state, dispatch] = useReducer(userListReportStateReducer, {...defaultUserListReportState});

    const setData = useCallback((value: GridData) => dispatch({
        type: UserListReportStateActionType.DataChanged,
        payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: UserListReportStateActionType.GridApiChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: UserListReportStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: UserListReportStateActionType.OrganizationChanged,
        payload: value
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: UserListReportStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: UserListReportStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    return {
        state,
        setData,
        setGridApi,
        setDownloadUid,
        setOrganization,
        setActions
    };
}

